/**
 * Administrator page / report deny-lists: client routing and labels.
 *
 * Policy (slugs, parsing, the role binding) is shared with the server via
 * `@valutten/shared-utils/adminAccess`. The server refuses the callables and
 * the rules refuse the direct reads; this file only keeps the UI from linking
 * to, or rendering, what the server will refuse. Read from the LIVE user doc
 * (`userData`), so a change shows on the next snapshot, like allowedReports.
 */

import {
  ADMIN_DENIABLE_REPORT_SLUGS,
  adminPageDenied,
  adminReportDenied,
  type AdminAccessContext,
  type AdminDeniableReportSlug,
  type AdminPageSlug,
} from '@valutten/shared-utils/adminAccess';
import { REPORT_REGISTRY } from './reportRegistry';

export { adminPageDenied, adminReportDenied, ADMIN_DENIABLE_REPORT_SLUGS };
export type { AdminAccessContext, AdminDeniableReportSlug, AdminPageSlug };

export const ADMIN_PAGE_OPTIONS: ReadonlyArray<{ slug: AdminPageSlug; label: string; description: string }> = [
  {
    slug: 'commissions',
    label: 'Commissions',
    description: 'The Commissions page: monthly company commission table.',
  },
  {
    slug: 'commission-settings',
    label: 'Commission Settings',
    description: 'Broker splits, base salaries, pay reconciliation and targets.',
  },
  {
    slug: 'dashboard-commissions',
    label: 'Dashboard commission figures',
    description:
      'Company upfront / trail / total commission and the per-broker revenue breakdown on the dashboards. Loan book, line of trail and settlements stay visible.',
  },
];

/** Admin-only reports that are not in REPORT_REGISTRY. */
const ADMIN_ONLY_REPORTS: ReadonlyArray<{ slug: AdminDeniableReportSlug; label: string; path: string }> = [
  { slug: 'broker-loan-books', label: 'Broker Loan Books', path: '/reports/broker-loan-books' },
  { slug: 'recludo', label: 'Recludo Reports', path: '/reports/recludo' },
];

const DENIABLE = new Set<string>(ADMIN_DENIABLE_REPORT_SLUGS);

/** Every report an administrator can be denied, with its label and route. */
export const ADMIN_REPORT_OPTIONS: ReadonlyArray<{ slug: AdminDeniableReportSlug; label: string; path: string }> = [
  ...REPORT_REGISTRY.filter((d) => DENIABLE.has(d.slug)).map((d) => ({
    slug: d.slug as AdminDeniableReportSlug,
    label: d.label,
    path: d.path,
  })),
  { slug: 'approved-vs-settled', label: 'Approved vs Settled', path: '/reports/approved-vs-settled' },
  ...ADMIN_ONLY_REPORTS,
];

const PAGE_PATHS: ReadonlyArray<[string, AdminPageSlug]> = [
  ['/commission-breakdown', 'commissions'],
  ['/broker-commission-settings', 'commission-settings'],
];

const matches = (pathname: string, base: string) => pathname === base || pathname.startsWith(`${base}/`);

/**
 * Whether this administrator may not open `pathname`. The single routing
 * chokepoint (AppLayout) and the menus both use it, so a deep link and a menu
 * entry can never disagree.
 */
export function adminPathDenied(ctx: AdminAccessContext | null | undefined, pathname: string): boolean {
  if (ctx?.role !== 'administrator') return false;
  for (const [base, page] of PAGE_PATHS) {
    if (matches(pathname, base)) return adminPageDenied(ctx, page);
  }
  for (const report of ADMIN_REPORT_OPTIONS) {
    if (matches(pathname, report.path)) return adminReportDenied(ctx, report.slug);
  }
  return false;
}
